import { useCallback, useEffect, useRef, useState } from "react";

const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);

/**
 * Animate a number from its current value up to `target` over `duration` ms,
 * starting only once the observed element scrolls into view (IntersectionObserver,
 * armed once). If `target` changes afterwards (e.g. live polling), it re-animates
 * from the current value.
 *
 * Usage: const { ref, value } = useCountUp(target, 1800);  <el ref={ref}>{value}</el>
 */
export function useCountUp(target, duration = 1800) {
  const [value, setValue] = useState(0);
  const [node, setNode] = useState(null); // callback ref → re-runs effect once the element mounts
  const [armed, setArmed] = useState(false); // entered the viewport at least once
  const fromRef = useRef(0);
  const rafRef = useRef(null);

  const ref = useCallback((el) => setNode(el), []);

  // Arm the animation on the first viewport entry, then stop observing.
  useEffect(() => {
    if (!node || armed) return undefined;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setArmed(true);
          io.disconnect();
        }
      },
      { threshold: 0.3 }
    );
    io.observe(node);
    return () => io.disconnect();
  }, [node, armed]);

  // Run the count-up once armed and a valid target is available.
  useEffect(() => {
    if (!armed || typeof target !== "number" || !Number.isFinite(target)) return undefined;
    const from = fromRef.current;
    const to = target;
    if (from === to) return undefined;
    const start = performance.now();
    const tick = (now) => {
      const p = Math.min((now - start) / duration, 1);
      setValue(Math.round(from + (to - from) * easeOutCubic(p)));
      if (p < 1) {
        rafRef.current = requestAnimationFrame(tick);
      } else {
        fromRef.current = to;
      }
    };
    cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [armed, target, duration]);

  return { ref, value };
}

export default useCountUp;
